import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import Modal from '../components/Modal';
import HABookingStatus from '../components/HABookingStatus';
import SemblePhysioBookingStatus from '../components/SemblePhysioBookingStatus';
import AppointmentCard from '../components/AppointmentCard';
import { useHealthAssessmentCtx } from '../contexts';
import useSemblePhysioBookings from '../hooks/useSemblePhysioBookings';
import './HealthAssessmentBookings.sass';
import ordinalNumber from '../utils/ordinalNumber';
import isUkBankHoliday from '../utils/isUKBankHoliday';
import { ReactComponent as InfoIcon } from '../static/img/info.svg';
import AddToCalendar from 'react-add-to-calendar';
export default () => {
  // Get IDaaS ID from query params
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const idaasId = searchParams.get('idaas-id');

  const {
    haCtxSelectors: { appointments, appointmentCancelled: haAppointmentCancelled },
    haCtxActions,
  } = useHealthAssessmentCtx();
  const haAppointmentData = appointments();

  const {
    appointments: physioAppointmentData,
    appointmentCancelled: physioAppointmentCancelled,
    resetCancelled: resetPhysioCancelled,
    getAppointments: getPhysioAppointments,
    cancelAppointment: cancelPhysioAppointment,
  } = useSemblePhysioBookings();

  const [selectedTab, setSelectedTab] = useState('upcoming');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedType, setSelectedType] = useState('all');
  const [selectedYear, setSelectedYear] = useState('all');
  const [appointmentWithinThreeWorkingDays, setAppointmentWithinThreeWorkingDays] = useState(false);

  const ITEMS_PER_PAGE = 3;
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 8 }, (_, i) => currentYear - i);

  useEffect(() => {
    if (idaasId) haCtxActions.getAppointments(idaasId, 'HA');
    getPhysioAppointments(idaasId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
  setCurrentPage(1);
  setSelectedType('all');
  setSelectedYear('all');
}, [selectedTab]);
useEffect(() => {
    const checkAppointments = async () => {
      const result = await hasAppointmentWithinThreeWorkingDays();
      setAppointmentWithinThreeWorkingDays(result);
    };

    checkAppointments();
  }, [haAppointmentData]);

  const haDetailsRef = useRef();
  const haCancelRef = useRef();
  const physioDetailsRef = useRef();
  const physioCancelRef = useRef();
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [selectedSource, setSelectedSource] = useState(null);

  const handleViewDetails = (event, appointmentData, source) => {
    event.preventDefault();
    setSelectedAppointment(appointmentData);
    setSelectedSource(source);
    (source === 'ha' ? haDetailsRef : physioDetailsRef).current.show();
  };

  const handleCancelAppointment = (event, appointmentData, source) => {
    event.preventDefault();
    setSelectedAppointment(appointmentData);
    setSelectedSource(source);
    (source === 'ha' ? haCancelRef : physioCancelRef).current.show();
  };

  const closeCancelModalAndReload = ref => {
    ref.current.hide();
    window.location.reload();
  };

  const hasUpcomingHaAppointment = (haAppointmentData ?? []).some(appointment => {
    const startDate = new Date(appointment['appointment-start-datetime']);
    return startDate > new Date() && appointment['appointment-status'] !== 'cancelled';
  });

  async function hasAppointmentWithinThreeWorkingDays() {
    const now = new Date();
    let dayCount = 0;
    const futureDate = new Date(now);
    while (dayCount < 3) {
      futureDate.setDate(futureDate.getDate() + 1);
      const dayOfWeek = futureDate.getDay();
      // ignore saturday/sunday/bank holidays in days to check
      if (dayOfWeek !== 0 && dayOfWeek !== 6 && !(await isUkBankHoliday(futureDate))) {
        dayCount++;
      }
    }
    return (haAppointmentData ?? []).some(obj => {
      const startDate = new Date(obj['appointment-start-datetime']);
      return startDate > now && startDate < futureDate && obj['appointment-status'] !== 'cancelled';
    });
  }

  const mergedAppointments = [
    ...(haAppointmentData ?? []).map(appointment => ({ ...appointment, source: 'ha' })),
    ...(physioAppointmentData ?? []).map(appointment => ({ ...appointment, source: 'physio' })),
  ];

  const filteredAppointments = mergedAppointments
    .filter(appointment => {
      const startDate = new Date(appointment['appointment-start-datetime']);
      const status = appointment['appointment-status'];
      const now = new Date();

      if (selectedTab === 'upcoming') {
        return startDate > now && status !== 'cancelled';
      }

      const isPast = startDate <= now || status === 'cancelled';
      if (!isPast) return false;

      if (selectedType === 'ha' && appointment.source !== 'ha') return false;
      if (selectedType === 'physio' && appointment.source !== 'physio') return false;
      if (selectedYear !== 'all' && startDate.getFullYear() !== Number(selectedYear)) return false;

      return true;
    })
    .sort((a, b) => {
      const diff =
        new Date(a['appointment-start-datetime']) - new Date(b['appointment-start-datetime']);
      return selectedTab === 'past' ? -diff : diff;
    });

  const totalPages = Math.ceil(filteredAppointments.length / ITEMS_PER_PAGE);
  const paginatedAppointments =
    selectedTab === 'past'
      ? filteredAppointments.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE)
      : filteredAppointments;

  const getPageNumbers = total => {
    if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1);
    const pageSet = new Set([1, 2, currentPage - 1, currentPage, currentPage + 1, total - 1, total]);
    const pages = [...pageSet].filter(p => p >= 1 && p <= total).sort((a, b) => a - b);
    const result = [];
    for (let i = 0; i < pages.length; i++) {
      if (i > 0 && pages[i] - pages[i - 1] > 1) result.push('...');
      result.push(pages[i]);
    }
    return result;
  };

  return (
    <>
      <div className="ha-bookings">
        <div className={`page ${'m--past-bookings'}`}>
          <div className="tabs__wrapper">
            <button
              className={`tab__item ${selectedTab === 'past' ? 'm--active' : ''}`}
              onClick={() => setSelectedTab('past')}
            >
              Past
            </button>
            <button
              className={`tab__item ${selectedTab === 'upcoming' ? 'm--active' : ''}`}
              onClick={() => setSelectedTab('upcoming')}
            >
              Upcoming
            </button>
          </div>

          {selectedTab === 'past' && (
            <div className="appointments-filter">
              <div className="appointments-filter__dropdowns">
                <div className="appointments-filter__group">
                  <label htmlFor="filter-type">Appointment type</label>
                  <select
                    id="filter-type"
                    value={selectedType}
                    onChange={e => setSelectedType(e.target.value)}
                  >
                    <option value="all">All appointments</option>
                    <option value="ha">Health Assessments</option>
                    <option value="physio">Physiotherapy</option>
                  </select>
                </div>
                <div className="appointments-filter__group">
                  <label htmlFor="filter-year">Year</label>
                  <select
                    id="filter-year"
                    value={selectedYear}
                    onChange={e => setSelectedYear(e.target.value)}
                  >
                    <option value="all">All years</option>
                    {years.map(year => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <p className="appointments-count">{filteredAppointments.length} past appointments</p>
            </div>
          )}

          {filteredAppointments.length === 0 && (
            <div className="no-items-found">
              {selectedTab === 'upcoming' ? 'No upcoming appointments' : 'No appointments found'}
            </div>
          )}

          {paginatedAppointments.map(
            ({
              ['appointment-id']: appointmentId,
              ['appointment-section-id']: appointmentSectionId,
              ['appointment-type-name']: appointmentTypeName,
              ['appointment-start-datetime']: appointmentStart,
              ['appointment-end-datetime']: appointmentEnd,
              ['appointment-locations']: locations,
              ['appointment-practitioners']: practitioners,
              ['appointment-status']: appointmentStatus,
              source,
            }) => {
              const startDate = new Date(appointmentStart);
              const endDate = new Date(appointmentEnd);
              const dayOfWeek = startDate.toLocaleString('en-GB', { weekday: 'long' });
              const month = startDate.toLocaleString('en-GB', { month: 'long' });
              const dayOfMonth = startDate.getDate();
              const year = startDate.getFullYear();
              const formattedDate = `${dayOfWeek} ${ordinalNumber(dayOfMonth)} ${month} ${year}`;
              const durationInMilliseconds = endDate - startDate;
              const durationInHours = durationInMilliseconds / (1000 * 60 * 60);
              const startTime = startDate.toTimeString().slice(0, 5);
              const endTime = endDate.toTimeString().slice(0, 5);
              const timeLabel = source === 'ha' ? `${startTime} - ${endTime}` : startTime;

              const detailsData = {
                appointmentTypeName,
                formattedDate,
                startTime,
                endTime,
                durationInHours,
                locations,
                practitioners,
                appointmentStart,
                appointmentEnd,
              };

              let footer = null;
              if (selectedTab === 'upcoming' && source === 'ha') {
                footer = (
                  <div className="cancellation-container">
                    <div className="cancellation-actions">
                      {!appointmentWithinThreeWorkingDays && (
                        <button
                          className="primary-btn m--red"
                          onClick={event =>
                            handleCancelAppointment(
                              event,
                              {
                                sectionId: appointmentSectionId,
                                formattedDate,
                                startTime,
                                appointmentTypeName,
                              },
                              'ha',
                            )
                          }
                        >
                          Cancel appointment
                        </button>
                      )}
                      <p>
                        If you would like to re-arrange your appointment please contact us as soon
                        as you can on <a href="tel:03001311550">0300 131 1550</a>
                      </p>
                    </div>
                    <div className="cancellation-message">
                      <p className="bold">
                        A 50% charge applies for cancellations or changes made with less than three
                        full working days&apos; notice. You&apos;ll be charged the full fee if you fail to
                        attend or arrive over 15 minutes late.
                      </p>
                    </div>
                    <p>
                      If you have a Health Assessment coming up, there&apos;s a few things you&apos;ll need to
                      do beforehand.{' '}
                      <a
                        target="_blank"
                        rel="noreferrer noopener"
                        className="link"
                        href="https://www.nuffieldhealth.com/health-assessments-hub/preparing-for-your-health-assessment-appointment"
                      >
                        Read our guide
                      </a>
                    </p>
                    <p>
                      Need help with your Health Assessment appointment?{' '}
                      <a
                        target="_blank"
                        rel="noreferrer noopener"
                        className="link"
                        href="https://www.nuffieldhealth.com/health-assessments-hub/our-health-assessments-faqs"
                      >
                        Read our FAQs
                      </a>
                    </p>
                  </div>
                );
              } else if (selectedTab === 'upcoming' && source === 'physio') {
                const isWithin24Hours = startDate - new Date() < 24 * 60 * 60 * 1000;
                footer = (
                  <div className="cancellation-container">
                    <div className="cancellation-actions">
                      {isWithin24Hours ? (
                        <>
                          <a href="tel:03450454845" className="secondary-btn phone-btn">
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.98 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.92 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 8.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                            </svg>
                            0345 045 4845
                          </a>
                          <p>
                            Need to make changes to your appointment? Please call us to cancel or
                            discuss your booking. Appointments within 24 hours cannot be cancelled
                            online.
                          </p>
                        </>
                      ) : (
                        <>
                          <button
                            className="primary-btn m--red"
                            onClick={event =>
                              handleCancelAppointment(
                                event,
                                {
                                  sectionId: appointmentSectionId,
                                  formattedDate,
                                  startTime,
                                  appointmentTypeName,
                                  appointmentStart,
                                },
                                'physio',
                              )
                            }
                          >
                            Cancel appointment
                          </button>
                          <p>
                            Need to make changes to your appointment? Please contact us on{' '}
                            <a href="tel:03450454845">0345 045 4845</a>
                          </p>
                        </>
                      )}
                    </div>
                    <div className="cancellation-message">
                      <p className="bold">
                        If you cancel or change an appointment with less than 24 hours&apos; notice or
                        fail to attend, a fee of up to £49 may be charged. Where treatment is
                        covered by insurance, you are personally responsible for this charge.
                      </p>
                    </div>
                    <p>
                      Need help with your Physiotherapy appointment?{' '}
                      <a
                        target="_blank"
                        rel="noreferrer noopener"
                        className="link"
                        href="https://www.nuffieldhealth.com/physiotherapy/faqs"
                      >
                        Read our FAQs
                      </a>
                    </p>
                  </div>
                );
              }

              return (
                <AppointmentCard
                  key={appointmentId}
                  timeLabel={timeLabel}
                  formattedDate={formattedDate}
                  appointmentTypeName={appointmentTypeName}
                  statusBadge={
                    source === 'ha' ? (
                      <HABookingStatus status={appointmentStatus} />
                    ) : (
                      <SemblePhysioBookingStatus status={appointmentStatus} />
                    )
                  }
                  locations={locations}
                  practitioners={practitioners}
                  onViewDetails={event => handleViewDetails(event, detailsData, source)}
                  footer={footer}
                  showDivider={selectedTab === 'upcoming'}
                />
              );
            },
          )}

          {selectedTab === 'past' && totalPages > 1 && (
            <div className="pagination">
              <button
                className="pagination__arrow"
                onClick={() => setCurrentPage(p => p - 1)}
                disabled={currentPage === 1}
                aria-label="Previous page"
              >
                &#8249;
              </button>
              {getPageNumbers(totalPages).map((page, index) =>
                page === '...' ? (
                  <span key={`ellipsis-${index}`} className="pagination__ellipsis">
                    &hellip;
                  </span>
                ) : (
                  <button
                    key={page}
                    className={`pagination__page ${currentPage === page ? 'm--active' : ''}`}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </button>
                ),
              )}
              <button
                className="pagination__arrow"
                onClick={() => setCurrentPage(p => p + 1)}
                disabled={currentPage === totalPages}
                aria-label="Next page"
              >
                &#8250;
              </button>
            </div>
          )}

          {selectedTab === 'upcoming' && !hasUpcomingHaAppointment && (
            <div>
              <h2 className="h2">Health assessment information</h2>
              <p>
                If you have a Health Assessment coming up, there&apos;s a few things you&apos;ll need to do
                beforehand.{' '}
                <a
                  target="_blank"
                  rel="noreferrer noopener"
                  className="link"
                  href="https://www.nuffieldhealth.com/health-assessments-hub/preparing-for-your-health-assessment-appointment"
                >
                  Read our guide
                </a>
              </p>
              <p>
                Need help with your Health Assessment appointment?{' '}
                <a
                  target="_blank"
                  rel="noreferrer noopener"
                  className="link"
                  href="https://www.nuffieldhealth.com/health-assessments-hub/our-health-assessments-faqs"
                >
                  Read our FAQs
                </a>
              </p>
            </div>
          )}

          <Modal type="haCancel" ref={haCancelRef}>
            {() =>
              !haAppointmentCancelled() && selectedAppointment ? (
                <div>
                  <h2 className="h2">Cancel your appointment?</h2>
                  <p>
                    Are you sure you want to cancel your {selectedAppointment.appointmentTypeName}{' '}
                    health assessment on {selectedAppointment.formattedDate}{' '}
                    {selectedAppointment.startTime}
                  </p>
                  <div className="cancellation-message">
                    <p>
                      A 50% charge applies for cancellations or changes made with less than three
                      full working days&apos; notice. You&apos;ll be charged the full fee if you fail to
                      attend or arrive over 15 minutes late.
                    </p>
                  </div>
                  <div className="btn-container">
                    <button className="secondary-btn" onClick={() => haCancelRef.current.hide()}>
                      No, go back
                    </button>
                    <button
                      className="primary-btn"
                      onClick={() => {
                        haCtxActions.cancelAppointment(selectedAppointment.sectionId);
                      }}
                    >
                      Cancel and continue
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <h2 className="h2">Your appointment has been cancelled.</h2>
                  <div className="btn-container">
                    <button
                      className="primary-btn"
                      onClick={() => closeCancelModalAndReload(haCancelRef)}
                    >
                      Close
                    </button>
                  </div>
                </div>
              )
            }
          </Modal>

          <Modal type="physioCancel" ref={physioCancelRef} onHide={resetPhysioCancelled}>
            {() =>
              !physioAppointmentCancelled && selectedAppointment ? (
                <div>
                  <h2 className="h2">Cancel your appointment?</h2>
                  <p>
                    Are you sure you want to cancel your{' '}
                    <strong>{selectedAppointment.appointmentTypeName}</strong> on{' '}
                    <strong>{selectedAppointment.formattedDate}</strong> at{' '}
                    <strong>{selectedAppointment.startTime}</strong>?
                  </p>
                  {selectedAppointment.appointmentStart && (() => {
                    const deadline = new Date(new Date(selectedAppointment.appointmentStart) - 24 * 60 * 60 * 1000);
                    const deadlineDate = deadline.toLocaleString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
                    const deadlineTime = deadline.toTimeString().slice(0, 5);
                    return (
                      <p className="physio-cancel-deadline">
                        <InfoIcon aria-hidden="true" />
                        You can cancel this appointment online until {deadlineDate} at {deadlineTime}. If you need to cancel after this time, please contact us on 0345 045 4845.
                      </p>
                    );
                  })()}
                  <div className="btn-container">
                    <button
                      className="secondary-btn"
                      onClick={() => physioCancelRef.current.hide()}
                    >
                      No, go back
                    </button>
                    <button
                      className="primary-btn"
                      onClick={() => {
                        cancelPhysioAppointment(selectedAppointment.sectionId);
                      }}
                    >
                      Cancel and continue
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <h2 className="h2">Appointment cancelled</h2>
                  <p>Your appointment has been successfully cancelled. You&apos;ll receive a confirmation email shortly.</p>
                  <div className="btn-container">
                    <button
                      className="primary-btn"
                      onClick={() => {
                        physioCancelRef.current.hide();
                        resetPhysioCancelled();
                      }}
                    >
                      Return to appointments
                    </button>
                  </div>
                </div>
              )
            }
          </Modal>

          <Modal type="haDetails" ref={haDetailsRef}>
            {() =>
              selectedSource === 'ha' &&
              selectedAppointment && (
                <div>
                  <div className="class-title bold">{selectedAppointment.appointmentTypeName}</div>
                  <br></br>
                  <table className="modal-details__table">
                    <tbody>
                      <tr>
                        <td>Date:</td>
                        <td>{selectedAppointment.formattedDate.toString()}</td>
                      </tr>
                      <tr>
                        <td>Time:</td>
                        <td>
                          <div>
                            {selectedAppointment.startTime.toString()} -{' '}
                            {selectedAppointment.endTime.toString()}
                          </div>
                          <p>Please aim to arrive 15 minutes before your appointment.</p>
                        </td>
                      </tr>
                      <tr>
                        <td>Duration:</td>
                        <td>{selectedAppointment.durationInHours} hours</td>
                      </tr>
                      <tr>
                        <td>Location:</td>
                        <td>
                          <div className="bold">
                            {selectedAppointment.locations[0]['location-display-name']}
                          </div>
                          <div>{selectedAppointment.locations[0]['location-name']}</div>
                          <div>{selectedAppointment.locations[0]['location-address-line-1']}</div>
                          <div>{selectedAppointment.locations[0]['location-address-line-2']}</div>
                          <div>{selectedAppointment.locations[0]['location-city']}</div>
                          <div>{selectedAppointment.locations[0]['location-postcode']}</div>
                        </td>
                      </tr>
                      <tr>
                        <td>Practitioner(s):</td>
                        <td>
                          <p>
                            For your assessment you may be viewed by multiple practitioners on the
                            day.
                          </p>
                          {selectedAppointment.practitioners.map((practitioner, index) => (
                            <div key={index}>{practitioner['practitioner-name']}</div>
                          ))}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )
            }
          </Modal>

          <Modal type="physioDetails" ref={physioDetailsRef}>
            {() =>
              selectedSource === 'physio' &&
              selectedAppointment && (
                <div>
                  <div className="class-title bold">{selectedAppointment.appointmentTypeName}</div>
                  <br></br>
                  <table className="modal-details__table">
                    <tbody>
                      <tr>
                        <td>Date:</td>
                        <td>{selectedAppointment.formattedDate.toString()}</td>
                      </tr>
                      <tr>
                        <td>Time:</td>
                        <td>
                          <div>{selectedAppointment.startTime.toString()}</div>
                          <p>Please aim to arrive 15 minutes before your appointment.</p>
                        </td>
                      </tr>
                      <tr>
                        <td>Duration:</td>
                        <td>typically {Math.round(selectedAppointment.durationInHours * 60)} minutes</td>
                      </tr>
                      {selectedAppointment.practitioners?.length > 0 && (
                        <tr>
                          <td>Practitioner(s):</td>
                          <td>
                            {selectedAppointment.practitioners.map((practitioner, index) => (
                              <div key={index}>{practitioner['practitioner-name']}</div>
                            ))}
                          </td>
                        </tr>
                      )}
                      {selectedAppointment.locations?.[0] && (
                        <tr>
                          <td>Location:</td>
                          <td>
                            <div className="bold">
                              {selectedAppointment.locations[0]['location-display-name']}
                            </div>
                            {selectedAppointment.locations[0]['location-address-line-1'] && (
                              <div>{selectedAppointment.locations[0]['location-address-line-1']}</div>
                            )}
                            {selectedAppointment.locations[0]['location-city'] && (
                              <div>{selectedAppointment.locations[0]['location-city']}</div>
                            )}
                            {selectedAppointment.locations[0]['location-postcode'] && (
                              <div>{selectedAppointment.locations[0]['location-postcode']}</div>
                            )}
                          </td>
                        </tr>
                      )}
                      {selectedTab === 'upcoming' && selectedAppointment.locations?.[0] && (
                        <tr>
                          <td>Directions to site:</td>
                          <td>
                            <a
                              className="map-link"
                              href={`https://maps.google.com/maps?q=${encodeURIComponent(
                                [
                                  selectedAppointment.locations[0]['location-display-name'],
                                  selectedAppointment.locations[0]['location-address-line-1'],
                                  selectedAppointment.locations[0]['location-city'],
                                  selectedAppointment.locations[0]['location-postcode'],
                                ]
                                  .filter(Boolean)
                                  .join(', ')
                              )}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              View location on map
                            </a>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  {selectedTab === 'upcoming' && (
                    <div className="link-wrapper">
                      <AddToCalendar
                        buttonLabel="Add to calendar"
                        event={{
                          title: selectedAppointment.appointmentTypeName,
                          location: [
                            selectedAppointment.locations?.[0]?.['location-display-name'],
                            selectedAppointment.locations?.[0]?.['location-address-line-1'],
                            selectedAppointment.locations?.[0]?.['location-city'],
                            selectedAppointment.locations?.[0]?.['location-postcode'],
                          ].filter(Boolean).join(', '),
                          startTime: selectedAppointment.appointmentStart,
                          endTime: selectedAppointment.appointmentEnd,
                        }}
                      />
                    </div>
                  )}
                  {selectedTab === 'upcoming' && (
                    <p>
                      Need help with your Physiotherapy appointment?{' '}
                      <a
                        target="_blank"
                        rel="noreferrer noopener"
                        className="link"
                        href="https://www.nuffieldhealth.com/physiotherapy/faqs"
                      >
                        Read our FAQs
                      </a>
                    </p>
                  )}
                </div>
              )
            }
          </Modal>
        </div>
      </div>
    </>
  );
};
